import type { Query } from '@tanstack/react-query';
import { type CollectionImageView, getPendingImageTimestamps, pollWhilePending } from '@vizit/api';
import type { SearchCollectionImagesQuery } from '@vizit/api';
import { useGetCollectionQuery, useSearchCollectionImagesQuery } from '@vizit/api';
import { Button, ButtonLevel, ButtonSize, Skeleton, Text, TextColor, TextSize, TextWeight } from '@vizit/atoms';
import { ARTIFACT_URLS_REFRESH_AFTER_MS, COMPARE_MAX_IMAGES, CollectionAssetToolbar } from '@vizit/collections';
import { awaitsTileScores, readTileScore } from '@vizit/collections';
import { useCollectionScoringControls } from '@vizit/collections';
import { useCustomBenchmarksAvailability } from '@vizit/feature-flags';
import { AlertCircleIcon, CompareIcon } from '@vizit/icons';
import { Asset, LibraryEmptyState, SortableGrid, ThumbnailSizeSlider } from '@vizit/molecules';
import { ClearableSelect, type ClearableSelectItem, CompareCard, HEATMAP_ARTIFACT_TYPES } from '@vizit/organisms';
import { HEATMAPS, HeatmapEducationStrip, HeatmapPicker } from '@vizit/organisms';
import { heatmapLabel } from '@vizit/organisms';
import { useThumbnailSlider } from '@vizit/organisms';
import * as React from 'react';
import { useStoredChoice, useStoredHeatmap } from '../../../hooks/use-stored-choice';
import { CollectionScoringControls } from '../collection-details-page/collection-scoring-controls';

enum CompareSortOption {
  Selection = 'selection',
  ScoreDesc = 'score-desc',
  ScoreAsc = 'score-asc',
  Added = 'added',
}

const SORT_ITEMS: ClearableSelectItem[] = [
  { id: CompareSortOption.Selection, label: 'Selection order' },
  { id: CompareSortOption.ScoreDesc, label: 'Score: high to low' },
  { id: CompareSortOption.ScoreAsc, label: 'Score: low to high' },
  { id: CompareSortOption.Added, label: 'Date added' },
];

const SCORE_SORTS: ReadonlySet<string> = new Set([CompareSortOption.ScoreDesc, CompareSortOption.ScoreAsc]);
const UNSCORED_SORT_ITEMS = SORT_ITEMS.filter((item) => !SCORE_SORTS.has(String(item.id)));

const COMPARE_DEFAULT_COLUMNS = 4;
const SORT_STORAGE_KEY = 'compare:sort';

function isCompareSortOption(value: unknown): value is CompareSortOption {
  return SORT_ITEMS.some((item) => item.id === value);
}

interface CompareItem {
  image: CollectionImageView;
  score: number | null;
  isScoring: boolean;
  needsCategory: boolean;
  selectionIndex: number;
}

const OVERLAY_SELECTOR = '[role="dialog"], [role="listbox"], [role="menu"]';
const DRAGGING_SELECTOR = '[data-dragging]';

function sortItems(items: CompareItem[], sort: CompareSortOption): CompareItem[] {
  const byScore = (direction: 1 | -1) => (a: CompareItem, b: CompareItem) => {
    if (a.score === null || b.score === null) return a.score === null ? (b.score === null ? 0 : 1) : -1;
    return direction * (a.score - b.score) || a.selectionIndex - b.selectionIndex;
  };
  const sorted = [...items];
  switch (sort) {
    case CompareSortOption.ScoreDesc:
      return sorted.sort(byScore(-1));
    case CompareSortOption.ScoreAsc:
      return sorted.sort(byScore(1));
    case CompareSortOption.Added:
      return sorted.sort((a, b) => (b.image.created_at ?? '').localeCompare(a.image.created_at ?? ''));
    default:
      return sorted.sort((a, b) => a.selectionIndex - b.selectionIndex);
  }
}

function highestScoreImageId(items: CompareItem[]): string | null {
  const scored = items.filter((item) => item.score !== null);
  if (scored.length < 2) return null;
  const top = Math.max(...scored.map((item) => item.score ?? 0));
  const leaders = scored.filter((item) => item.score === top);
  return leaders.length === 1 ? (leaders[0]?.image.id ?? null) : null;
}

function isTypingTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))
  );
}

interface ComparePageProps {
  collectionId: string;
  workspaceId?: string;
  imageIds: string[];
  onImageIdsChange: (imageIds: string[]) => void;
  onBackToCollection: () => void;
  /** The benchmark picked on the collection that it has not recorded, as a picker key (URL param `benchmark`) */
  pickedBenchmarkKey?: string | null;
  /** Called with each benchmark the user picks, and with `null` once the kept one is stale or recorded */
  onPickedBenchmarkKeyChange?: (key: string | null) => void;
  /** Opens an asset, with the benchmark shown when it has no category to score the asset against */
  onOpenAsset: (imageId: string, awaitingCategoryBenchmarkKey: string | null) => void;
}

function ComparePage(props: ComparePageProps) {
  const { collectionId, workspaceId, imageIds, onImageIdsChange, onBackToCollection, onOpenAsset } = props;
  const { pickedBenchmarkKey = null, onPickedBenchmarkKeyChange } = props;
  const ids = React.useMemo(() => imageIds.slice(0, COMPARE_MAX_IMAGES), [imageIds]);
  const benchmarksAvailability = useCustomBenchmarksAvailability(workspaceId);
  const showCustomBenchmarks = benchmarksAvailability.isEnabled;
  const [heatmap, setHeatmap] = useStoredHeatmap();
  const [sort, setSort] = useStoredChoice(SORT_STORAGE_KEY, isCompareSortOption, CompareSortOption.Selection);
  const thumbnailSlider = useThumbnailSlider('compare', { isEnabled: true, defaultColumns: COMPARE_DEFAULT_COLUMNS });

  const {
    data: collection,
    isPending: isCollectionPending,
    isLoadingError: isCollectionError,
  } = useGetCollectionQuery(
    { workspace_id: workspaceId ?? '', collection_id: collectionId },
    { enabled: !!workspaceId },
  );
  const scoring = useCollectionScoringControls({
    workspaceId,
    collectionId,
    imageType: collection?.image_type,
    lastScoring: collection?.last_scoring,
    defaultBenchmark: collection?.default_benchmark,
    collectionCategoryId: collection?.product_category_id,
    isCollectionPending,
    isCollectionError,
    enabled: showCustomBenchmarks,
    pickedBenchmarkKey,
    onPickedBenchmarkKeyChange,
  });
  const { awaitingCategoryBenchmarkKey } = scoring;
  const gridScoring = showCustomBenchmarks ? scoring.gridScoring : null;
  const pair = gridScoring?.pair ?? null;
  // The images wait for the API to say whether the organisation has benchmarks, so their own scores never show first.
  const isPairResolved = !benchmarksAvailability.isChecking && (!gridScoring || gridScoring.isResolved);

  const { data, isPending, isError, isPlaceholderData, dataUpdatedAt, refetch } = useSearchCollectionImagesQuery(
    {
      workspace_id: workspaceId ?? '',
      collection_id: collectionId,
      image_ids: ids,
      limit: COMPARE_MAX_IMAGES,
      include_artifacts: HEATMAP_ARTIFACT_TYPES,
      ...pair,
    },
    {
      enabled: !!workspaceId && ids.length >= 2 && isPairResolved,
      placeholderData: (previous) => previous,
      refetchInterval: pollWhilePending((query: Query<SearchCollectionImagesQuery>) =>
        getPendingImageTimestamps(query.state.data?.data ?? [], { awaitsScores: awaitsTileScores(gridScoring) }),
      ),
    },
  );

  React.useEffect(() => {
    if (!data || isPlaceholderData) return;
    const returnedIds = new Set(data.data.map((image) => image.id));
    const keptIds = ids.filter((id) => returnedIds.has(id));
    if (keptIds.length < ids.length) onImageIdsChange(keptIds);
  }, [data, isPlaceholderData, ids, onImageIdsChange]);

  const refreshExpiredArtifactUrls = React.useCallback(() => {
    if (Date.now() - dataUpdatedAt > ARTIFACT_URLS_REFRESH_AFTER_MS) {
      void refetch({ cancelRefetch: false });
    }
  }, [dataUpdatedAt, refetch]);

  const items = React.useMemo(() => {
    const byId = new Map((data?.data ?? []).map((image) => [image.id, image]));
    return ids.flatMap((id, selectionIndex) => {
      const image = byId.get(id);
      if (!image) return [];
      const {
        score,
        isPending: isScorePending,
        isUnscored,
        needsCategory,
      } = readTileScore(image, pair, {
        isAwaitingUpload: gridScoring?.waitingImageIds?.includes(id),
        pairRequestedAt: gridScoring?.requestedAt,
        isCustomBenchmark: gridScoring?.isCustomBenchmark,
        needsCategory: gridScoring?.needsCategory,
      });
      return [
        {
          image,
          score: isUnscored ? null : score,
          isScoring: isScorePending && !isUnscored,
          needsCategory,
          selectionIndex,
        },
      ];
    });
  }, [data, ids, pair, gridScoring]);
  // Without a pair under a custom benchmark, or with no category at all, the cards have no score to sort by unless an
  // image has its own, so the score sorts give way to selection order.
  const isScoreSortAvailable = awaitsTileScores(gridScoring) || items.some((item) => item.score !== null);
  const effectiveSort = isScoreSortAvailable || !SCORE_SORTS.has(sort) ? sort : CompareSortOption.Selection;
  const sortedItems = React.useMemo(() => sortItems(items, effectiveSort), [items, effectiveSort]);
  const itemsById = React.useMemo(() => new Map(items.map((item) => [item.image.id, item])), [items]);
  const highestId = highestScoreImageId(items);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || isTypingTarget(event.target) || event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      if (document.querySelector(`${DRAGGING_SELECTOR}, ${OVERLAY_SELECTOR}`)) return;
      const picked = HEATMAPS[Number(event.key) - 1];
      if (picked) {
        setHeatmap(picked.id);
        return;
      }
      if (event.key === 'Escape') onBackToCollection();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [setHeatmap, onBackToCollection]);

  const isWaiting = ids.length >= 2 && (isPending || !isPairResolved);
  // A disabled read still serves the cards cached on an earlier visit, scored by the images' own scores.
  const showLoadingCards = isWaiting && (items.length === 0 || benchmarksAvailability.isChecking);
  const hasNothingToCompare = ids.length < 2 || (!isWaiting && !isError && items.length < 2);
  const imageTypeLabel = collection?.image_type === 'hero' ? 'Hero assets' : 'Carousel assets';
  const assetCount = isWaiting && items.length === 0 ? ids.length : items.length;
  const gridClassName = thumbnailSlider.gridColumns
    ? 'grid gap-x-12 gap-y-16'
    : 'grid grid-cols-2 gap-x-12 gap-y-16 lg:grid-cols-4';
  const gridStyle = thumbnailSlider.gridColumns
    ? { gridTemplateColumns: `repeat(${thumbnailSlider.gridColumns}, minmax(0, 1fr))` }
    : undefined;

  if (isError && !data) {
    return (
      <div className="flex flex-col px-8 pt-12 pb-8 max-sm:px-4 max-sm:pt-8">
        <LibraryEmptyState
          heading="Couldn't load these assets"
          description="Something went wrong. Try again in a moment."
          icon={AlertCircleIcon}
        >
          <Button level={ButtonLevel.Secondary} size={ButtonSize.Sm} onPress={() => refetch()}>
            Try again
          </Button>
        </LibraryEmptyState>
      </div>
    );
  }

  if (hasNothingToCompare) {
    return (
      <div className="flex flex-col px-8 pt-12 pb-8 max-sm:px-4 max-sm:pt-8">
        <LibraryEmptyState
          heading="Nothing to compare yet"
          description="Select at least two assets in the collection, then choose Compare."
          icon={CompareIcon}
        >
          <Button level={ButtonLevel.Secondary} size={ButtonSize.Sm} onPress={onBackToCollection}>
            Back to collection
          </Button>
        </LibraryEmptyState>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 px-8 pt-12 pb-36 max-sm:px-4 max-sm:pt-8">
      <CollectionAssetToolbar>
        <CollectionAssetToolbar.Filters />
        <CollectionAssetToolbar.Controls>
          <CollectionAssetToolbar.Control label="Heat maps:">
            <HeatmapPicker heatmap={heatmap} onHeatmapChange={setHeatmap} />
          </CollectionAssetToolbar.Control>
          <CollectionAssetToolbar.Control label="Sort by:">
            <ClearableSelect
              ariaLabel="Sort compared assets"
              placeholder="Selection order"
              items={isScoreSortAvailable ? SORT_ITEMS : UNSCORED_SORT_ITEMS}
              selectedKey={effectiveSort}
              onSelectionChange={(key) => isCompareSortOption(key) && setSort(key)}
              includeAllOption={false}
              isClearable={false}
              className="min-w-[200px]"
            />
          </CollectionAssetToolbar.Control>
        </CollectionAssetToolbar.Controls>
      </CollectionAssetToolbar>

      {benchmarksAvailability.isChecking ? (
        <Skeleton variant="square" className="h-10 w-1/3" />
      ) : showCustomBenchmarks ? (
        <CollectionScoringControls
          scoring={scoring}
          workspaceId={workspaceId}
          collectionId={collectionId}
          imageType={collection?.image_type}
          assetCount={assetCount}
        />
      ) : (
        <Text size={TextSize.Sm} weight={TextWeight.Medium} color={TextColor.Quaternary}>
          {`Showing ${assetCount} assets · ${imageTypeLabel}`}
        </Text>
      )}

      <HeatmapEducationStrip heatmap={heatmap} />

      {showLoadingCards ? (
        <div className={gridClassName} style={gridStyle} aria-busy="true">
          {ids.map((id) => (
            <Asset key={id} isLoading showScoreBar={false}>
              <Asset.Image name="Loading..." src={null} />
            </Asset>
          ))}
        </div>
      ) : (
        <SortableGrid
          items={sortedItems.map(({ image }) => image.id)}
          onReorder={onImageIdsChange}
          isDisabled={effectiveSort !== CompareSortOption.Selection}
          itemName={(id) => itemsById.get(id)?.image.name ?? id}
          className={gridClassName}
          style={gridStyle}
          renderItem={(id) => {
            const item = itemsById.get(id);
            if (!item) return null;
            const { image, score, isScoring, needsCategory } = item;
            const heatmapSrc = heatmap === 'original' ? null : image.artifacts?.[heatmap];
            return (
              <CompareCard
                name={image.name}
                imageSrc={image.url_360 ?? image.url}
                fallbackSrc={image.url}
                heatmapOverlay={
                  heatmapSrc
                    ? {
                        src: heatmapSrc,
                        alt: `${heatmapLabel(heatmap)} of ${image.name}`,
                        onError: refreshExpiredArtifactUrls,
                      }
                    : undefined
                }
                score={score}
                isScoring={isScoring}
                needsCategory={needsCategory}
                imageTypeLabel={imageTypeLabel}
                isHighest={image.id === highestId}
                onRemove={() => onImageIdsChange(ids.filter((imageId) => imageId !== image.id))}
                onOpen={() => onOpenAsset(image.id, awaitingCategoryBenchmarkKey)}
              />
            );
          }}
        />
      )}

      {thumbnailSlider.isVisible && (
        <ThumbnailSizeSlider
          columns={thumbnailSlider.columns}
          onColumnsChange={thumbnailSlider.setColumns}
          className="fixed bottom-12 right-12 z-30"
        />
      )}
    </div>
  );
}

export { ComparePage, CompareSortOption };
export type { ComparePageProps };
